import { useEffect, useMemo, useState } from 'react';
import {
  ChevronDown, ChevronLeft, ChevronRight, ChevronUp, Expand,
  Image as ImageIcon, MapPin, Plus, Trash2, Upload,
} from 'lucide-react';
import { getHolidayTypes } from '../../../../../api/geo/useHolidayTypes';
import HotelPicker from './HotelPicker';
import DestinationPicker, { destKey } from './DestinationPicker';
import BoardCodeDropdown from '../../../../../components/Booking/BoardCodeDropdown';
import HolidayTypeLiveCountries from './HolidayTypeLiveCountries';
import VacationSearchEditor from './VacationSearchEditor';
import MarqueeItemCard from './MarqueeItemCard';
import TravelIconPicker, { ToneSwatches } from './TravelIconPicker';
import {
  autoLinkIcon,
  combinedSearchUrl,
  defaultViewAllLabel,
  isEmoji,
  isGlyph,
  resolveGroupStyle,
} from './travelIcons';
import SelectBox from '../../../../../components/BaseComponents/SelectBox/SelectBox';
import ToggleSwitch from '../../../../../components/BaseComponents/Toggle/ToggleSwitch';
import BaseModal from '../../../../../components/BaseComponents/BaseModal/BaseModal';
import { UPLOAD_BASE_URL } from '../../../../../utils/ip';
import {
  BADGE_CUSTOM_VALUE,
  BADGE_NONE_VALUE,
  DESTINATION_BADGE_OPTIONS,
  DESTINATION_BADGE_PRESET_OPTIONS,
  EMPTY_AWARD,
  EMPTY_FEATURED_HOLIDAY_TYPE,
  MAX_FEATURED_HOLIDAY_TYPES,
  MAX_FEATURED_DESTINATIONS,
  MAX_POPULAR_HOTELS,
  EMPTY_DEST_CARD,
  EMPTY_DEST_TAB,
  EMPTY_GROUP,
  EMPTY_HOTEL,
  EMPTY_REVIEW,
  EMPTY_SEARCH_FIELD,
  EMPTY_STAT,
  EMPTY_TRUST,
  EMPTY_VACATION,
} from '../HomepageSettings.constants';
import styles from '../HomepageSettings.module.css';

function resolveImageUrl(url) {
  if (!url) return '';
  // Already a full URL (OVH CDN, external, etc.)
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  // Absolute server path → prepend base (e.g. /uploads/homepage/file.webp)
  if (url.startsWith('/')) return `${UPLOAD_BASE_URL}${url}`;
  // Relative storage key (e.g. homepage/file.webp) — try /uploads/ path on backend
  return `${UPLOAD_BASE_URL}/uploads/${url}`;
}

function isPresetBadge(value) {
  return DESTINATION_BADGE_PRESET_OPTIONS.some((opt) => opt.value === value);
}

function getBadgeSelectValue(value) {
  if (!value) return BADGE_NONE_VALUE;
  if (isPresetBadge(value)) return value;
  return BADGE_CUSTOM_VALUE;
}

export default function HomepageSettingsSections({
  config,
  updateField,
  uploadLoading,
  uploadError,
  helpers,
}) {
  // Which hero banner the full-size preview is open for ('backgroundImageUrl',
  // 'flightBackgroundImageUrl'), or null for none. Holding the field rather than a boolean
  // lets the two banners share one viewer instead of one modal each.
  const [heroImageViewer, setHeroImageViewer] = useState(null);

  // ── Section accordion ─────────────────────────────────────────────────────
  // Fourteen stacked sections made this page a very long scroll. Each one now
  // collapses to its header; clicking a title opens it and closes the others,
  // so only one body is ever on screen.
  const [openSection, setOpenSection] = useState(null);
  const toggleOpenSection = (key) => setOpenSection((cur) => (cur === key ? null : key));
  const secCls = (key) =>
    `${styles.card} ${openSection === key ? styles.cardOpen : styles.cardCollapsed}`;

  /**
   * One hero banner: URL box, upload button, thumbnail and full-size preview.
   *
   * The homepage has a banner per search tab and they are edited the same way, so this is
   * called once per field rather than duplicated. A plain function, not a component — a
   * component declared here would be a new type on every render and would remount the file
   * input mid-upload.
   */
  const heroImageField = ({ field, label, hint }) => {
    const value = config.hero?.[field] ?? '';
    const set = (url) => updateField('hero', { ...(config.hero || {}), [field]: url });

    return (
      <div key={field} className={`${styles.field} ${styles.heroBackgroundField}`}>
        <label>{label}</label>
        <div className={styles.heroBackgroundPanel}>
          <div className={styles.heroBackgroundControlRow}>
            <div className={styles.heroBackgroundInputWrap}>
              <input
                value={value}
                placeholder="Upload or paste image URL"
                onChange={(e) => set(e.target.value)}
              />
            </div>
            <div className={styles.heroBackgroundButtons}>
              <label className={styles.uploadBtn}>
                <Upload size={14} />
                {uploadLoading ? 'Uploading...' : 'Upload'}
                <input
                  type="file"
                  accept="image/png,image/jpeg,image/jpg,image/svg+xml,image/webp"
                  disabled={uploadLoading}
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    handleImageUpload(file, (url) => set(url));
                    e.target.value = '';
                  }}
                />
              </label>
            </div>
          </div>

          {value ? (
            <div className={styles.heroBackgroundPreviewCard}>
              <div className={styles.imagePreviewWrap}>
                <img className={styles.heroImagePreview} src={resolveImageUrl(value)} alt={`${label} preview`} />
                <button
                  type="button"
                  className={styles.previewActionBtn}
                  onClick={() => setHeroImageViewer(field)}
                >
                  <Expand size={14} />
                  View Full
                </button>
              </div>
            </div>
          ) : (
            <div className={styles.heroBackgroundEmpty}>Upload an image to preview it here.</div>
          )}
        </div>
        <span className={styles.fieldHint}>{hint}</span>

        {value && (
          <BaseModal
            open={heroImageViewer === field}
            onClose={() => setHeroImageViewer(null)}
            title={`${label} preview`}
            width="min(96vw, 1200px)"
            footer={
              <div className={styles.modalActions}>
                <button type="button" className={styles.modalBtn} onClick={() => setHeroImageViewer(null)}>
                  Close
                </button>
              </div>
            }
          >
            <div className={styles.fullImageWrap}>
              <img className={styles.fullImage} src={resolveImageUrl(value)} alt={`${label} full preview`} />
            </div>
          </BaseModal>
        )}
        {uploadError && <span className={styles.uploadError}>{uploadError}</span>}
      </div>
    );
  };
  const secTitle = (key, label) => (
    <button
      type="button"
      className={styles.cardTitleBtn}
      aria-expanded={openSection === key}
      onClick={() => toggleOpenSection(key)}
    >
      <ChevronRight size={15} className={styles.cardTitleChev} />
      <h3>{label}</h3>
    </button>
  );
  const {
    ensureArray,
    updateListItem,
    addListItem,
    removeListItem,
    updatePrimitiveListItem,
    updateDestinationCardField,
    handleImageUpload,
  } = helpers;

  // ── Section visibility toggles ────────────────────────────────────────────
  const visibility = config.sectionVisibility || {};
  const isSectionVisible = (key) => visibility[key] !== false;
  const toggleSection = (key) => {
    updateField('sectionVisibility', { ...visibility, [key]: !isSectionVisible(key) });
  };

  // ── Featured holiday types (the 4 homepage "Travel Categories" cards) ──────
  const [holidayTypes, setHolidayTypes] = useState([]);
  useEffect(() => {
    let cancelled = false;
    getHolidayTypes({ limit: 200 })
      .then((res) => {
        if (cancelled) return;
        const list = Array.isArray(res?.data) ? res.data : [];
        setHolidayTypes(list.filter((t) => t?.active !== false));
      })
      .catch(() => { /* picker degrades to the stored titles */ });
    return () => { cancelled = true; };
  }, []);

  const featured = ensureArray(config.featuredHolidayTypes);
  const featuredFull = featured.length >= MAX_FEATURED_HOLIDAY_TYPES;

  const holidayTypeOptions = useMemo(
    () => holidayTypes.map((t) => ({ value: String(t.id), label: t.name })),
    [holidayTypes]
  );

  // Pick a holiday type for a slot: store the id AND cache its name, so the card
  // still labels correctly in the dashboard if the types API is unavailable.
  const selectFeaturedType = (index, value) => {
    const picked = holidayTypes.find((t) => String(t.id) === String(value));
    const next = featured.map((item, i) =>
      i === index
        ? { ...item, holidayTypeId: picked ? picked.id : null, title: picked ? picked.name : '' }
        : item
    );
    updateField('featuredHolidayTypes', next);
  };

  const moveFeatured = (index, delta) => {
    const target = index + delta;
    if (target < 0 || target >= featured.length) return;
    const next = [...featured];
    [next[index], next[target]] = [next[target], next[index]];
    updateField('featuredHolidayTypes', next);
  };

  // ── Destinations under a featured holiday type ────────────────────────────
  // Stored on the item as `destinations: [{ type, code, name, countryName }]`.
  // The website turns each one into a /results link filtered by that code.
  const destsOf = (item) => ensureArray(item?.destinations);

  const setDestinations = (index, nextList) =>
    updateListItem('featuredHolidayTypes', index, 'destinations', nextList);

  const addDestination = (index, dest) => {
    const current = destsOf(featured[index]);
    if (current.length >= MAX_FEATURED_DESTINATIONS) return;
    if (current.some((d) => destKey(d) === destKey(dest))) return;
    setDestinations(index, [...current, dest]);
  };

  const removeDestination = (index, di) =>
    setDestinations(index, destsOf(featured[index]).filter((_, i) => i !== di));

  const moveDestination = (index, di, delta) => {
    const current = destsOf(featured[index]);
    const target = di + delta;
    if (target < 0 || target >= current.length) return;
    const next = [...current];
    [next[di], next[target]] = [next[target], next[di]];
    setDestinations(index, next);
  };

  // Which cards have their destinations panel open. Collapsed by default so a
  // full list of 4 cards stays scannable; the header carries a summary so you
  // can see what is set without expanding.
  const [openDests, setOpenDests] = useState(() => new Set());
  const toggleDests = (index) =>
    setOpenDests((prev) => {
      const next = new Set(prev);
      if (next.has(index)) next.delete(index); else next.add(index);
      return next;
    });

  // The public URL is derived from the holiday type NAME on every request (there
  // is no slug column), so mirror the backend's toSlug here. Prefers the live
  // name over the cached title, which can be stale after a rename.
  const slugForItem = (item) => {
    const live = holidayTypes.find((t) => String(t.id) === String(item?.holidayTypeId));
    return String(live?.name || item?.title || '')
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '') || 'holiday-type';
  };

  // ── Popular destination group links ───────────────────────────────────────
  // A link is a legacy plain string or an object carrying what to filter by, and
  // optionally the emoji it shows ('' leaves the website to pick one).
  const asLinkObject = (link) =>
    typeof link === 'string' || link == null
      ? { label: String(link || ''), dest: null, boardCode: '', holidayTypeId: null, icon: '' }
      : {
          label: link.label ?? '',
          dest: link.dest ?? null,
          boardCode: link.boardCode ?? '',
          holidayTypeId: link.holidayTypeId ?? null,
          icon: link.icon ?? '',
        };

  // Mirrors the website's groupLinkUrl so the editor can preview the real target.
  const groupLinkPreview = (l) => {
    const qs = new URLSearchParams();
    if (l.dest?.code) qs.set(l.dest.type === 'country' ? 'countries' : 'destinations', l.dest.code);
    if (l.boardCode) qs.set('boards', l.boardCode);
    if (l.holidayTypeId != null && l.holidayTypeId !== '') qs.set('themes', String(l.holidayTypeId));
    if ([...qs.keys()].length === 0) return null;
    if (l.label) qs.set('destinationLabel', l.label);
    return `/results?${qs.toString()}`;
  };

  const setGroupLinks = (groupIndex, nextLinks) =>
    updateListItem('popularDestinationGroups', groupIndex, 'links', nextLinks);

  const patchGroupLink = (groupIndex, linkIndex, patch) => {
    const links = ensureArray(config.popularDestinationGroups?.[groupIndex]?.links);
    setGroupLinks(
      groupIndex,
      links.map((l, i) => (i === linkIndex ? { ...asLinkObject(l), ...patch } : l))
    );
  };

  const addGroupLink = (groupIndex) => {
    const links = ensureArray(config.popularDestinationGroups?.[groupIndex]?.links);
    setGroupLinks(groupIndex, [...links, { label: '', dest: null, boardCode: '', holidayTypeId: null }]);
  };

  const removeGroupLink = (groupIndex, linkIndex) => {
    const links = ensureArray(config.popularDestinationGroups?.[groupIndex]?.links);
    setGroupLinks(groupIndex, links.filter((_, i) => i !== linkIndex));
  };

  // The card's "View all": its own text and filters, or none of either to let the website
  // search every place in the card.
  const patchViewAll = (groupIndex, patch) =>
    updateListItem('popularDestinationGroups', groupIndex, 'viewAll', {
      ...asLinkObject(config.popularDestinationGroups?.[groupIndex]?.viewAll),
      ...patch,
    });

  // Place, board type and holiday type: the same three filters for a link and for "View all".
  const linkFilterFields = (l, onPatch) => (
    <div className={styles.groupLinkGrid}>
      <div className={styles.field}>
        <label>Place</label>
        {l.dest?.code ? (
          <span className={styles.destChip}>
            <span
              className={`${styles.destChipKind} ${
                l.dest.type === 'country' ? styles.destChipKindCountry : styles.destChipKindCity
              }`}
            >
              {l.dest.type === 'country' ? 'Country' : 'City'}
            </span>
            <span className={styles.destChipName}>{l.dest.name}</span>
            <span className={styles.destChipCode}>{l.dest.code}</span>
            <button
              type="button"
              className={`${styles.destChipBtn} ${styles.destChipRemove}`}
              title="Clear place"
              onClick={() => onPatch({ dest: null })}
            >
              <Trash2 size={12} />
            </button>
          </span>
        ) : (
          <DestinationPicker onPick={(dest) => onPatch({ dest })} />
        )}
      </div>

      <div className={styles.field}>
        <label>Board type</label>
        <BoardCodeDropdown
          value={l.boardCode || ''}
          placeholder="Any board"
          onChange={(code) => onPatch({ boardCode: code || '' })}
        />
      </div>

      <div className={styles.field}>
        <label>Holiday type</label>
        <SelectBox
          width="100%"
          value={l.holidayTypeId != null ? String(l.holidayTypeId) : ''}
          options={[{ value: '', label: 'Any holiday type' }, ...holidayTypeOptions]}
          placeholder="Any holiday type"
          onChange={(e) =>
            onPatch({ holidayTypeId: e.target.value === '' ? null : Number(e.target.value) })
          }
        />
      </div>
    </div>
  );

  // Sets several fields on one destination card in a SINGLE update. The shared
  // updateDestinationCardField reads `config` from its closure, so calling it
  // twice inside one handler makes the second call clobber the first.
  const patchDestinationCard = (tabIndex, cardIndex, patch) => {
    const tabs = ensureArray(config.destinationTabs);
    updateField(
      'destinationTabs',
      tabs.map((entry, i) =>
        i !== tabIndex
          ? entry
          : {
              ...entry,
              cards: ensureArray(entry.cards).map((c, ci) =>
                ci === cardIndex ? { ...c, ...patch } : c
              ),
            }
      )
    );
  };

  // ── Vacation type card search ─────────────────────────────────────────────
  // Patches one card's `search` and its `boardCode` mirror in a SINGLE update, for the same
  // reason patchDestinationCard exists: updateListItem closes over `config`, so two calls in
  // one handler would make the second clobber the first.
  //
  // The mirror keeps an already-cached website bundle working: that build only understands
  // `boardCode`, so a card whose criteria are exactly one board still opens today's URL.
  // Zero or several criteria have no honest single-board equivalent, and a card that does
  // nothing beats a card that opens the wrong search.
  const patchVacationSearch = (index, patch) => {
    const items = ensureArray(config.vacationTypes);
    updateField(
      'vacationTypes',
      items.map((entry, i) => {
        if (i !== index) return entry;
        const search = { ...(entry.search || {}), ...patch };
        const boards = ensureArray(search.criteria).filter((c) => c?.filterKey === 'boards');
        return { ...entry, search, boardCode: boards.length === 1 ? String(boards[0].value) : '' };
      })
    );
  };

  const destsSummary = (item) => {
    const list = destsOf(item);
    if (!list.length) return 'None set';
    const names = list.map((d) => d.name).filter(Boolean);
    const shown = names.slice(0, 3).join(', ');
    return names.length > 3 ? `${shown} +${names.length - 3}` : shown;
  };

  // ── Popular hotels (picked from the hotel database) ───────────────────────
  const popularHotels = ensureArray(config.popularHotels);
  const hotelsFull = popularHotels.length >= MAX_POPULAR_HOTELS;

  // Fill a card from a real hotel row. Score and price stay manual — they are
  // marketing values, not something the hotels table stores.
  //
  // hotelCode/destinationCode are stored alongside the internal hotelId because they are the
  // BOOKABLE identity: the website turns the card into a link to that hotel's live-priced page,
  // and both the price cache and the hotel URL are keyed by hotelCode, not by our row id.
  const applyHotelPick = (index, hotel, location) => {
    const next = popularHotels.map((item, i) =>
      i === index
        ? {
            ...item,
            hotelId: hotel.id,
            hotelCode: hotel.hotelCode != null ? String(hotel.hotelCode) : null,
            destinationCode: hotel.destinationCode || null,
            countryCode: hotel.countryCode || null,
            name: hotel.name || item.name,
            location: location || item.location,
            stars: hotel.stars || item.stars || 5,
            imageUrl: hotel.primaryImage || item.imageUrl || '',
          }
        : item
    );
    updateField('popularHotels', next);
  };

  const moveHotel = (index, delta) => {
    const target = index + delta;
    if (target < 0 || target >= popularHotels.length) return;
    const next = [...popularHotels];
    [next[index], next[target]] = [next[target], next[index]];
    updateField('popularHotels', next);
  };

  const logo = config.logo || {};
  const updateLogo = (key, value) => {
    updateField('logo', { ...logo, [key]: value });
  };
  /* ── Hero title ───────────────────────────────────────────────────────────
     Three boxes, not one string with markup hidden inside it.

     It used to be stored as "Vind jouw zon *bestemming*", and nothing in this
     form said that asterisks meant anything, so the only way to find out which
     word the site would set in gold was to save and go and look. SUNSKY
     highlighted the wrong word for exactly that reason.

     The three parts are the stored value now. `title` is still written and kept
     in step with them, because an older website build — and anything else that
     reads the single field — has to keep showing the same words.             */
  const heroTitleParts = (() => {
    const hero = config.hero || {};
    if (
      hero.titleBefore != null ||
      hero.titleHighlight != null ||
      hero.titleAfter != null
    ) {
      return {
        before: hero.titleBefore ?? '',
        highlight: hero.titleHighlight ?? '',
        after: hero.titleAfter ?? '',
      };
    }
    // Nothing split yet: read the old single field, so opening this section for
    // the first time shows the title that is live rather than three empty boxes.
    const raw = String(hero.title ?? '');
    const marked = /^([\s\S]*?)\*([^*]+)\*([\s\S]*)$/.exec(raw);
    if (marked) {
      return { before: marked[1].trim(), highlight: marked[2].trim(), after: marked[3].trim() };
    }
    // No asterisks anywhere: the website accents the word for sun wherever it
    // appears, which is what the live title relies on today. Splitting on that
    // keeps the gold word the visitor already sees. Handing back the whole
    // string with an empty highlight would drop the accent the moment anything
    // in this section was saved, and nobody would connect the two.
    const auto = /^([\s\S]*?)\b(sun|zon)\b([\s\S]*)$/i.exec(raw);
    if (auto) {
      return { before: auto[1].trim(), highlight: auto[2], after: auto[3].trim() };
    }
    return { before: raw, highlight: '', after: '' };
  })();

  const setHeroTitlePart = (part, value) => {
    const parts = { ...heroTitleParts, [part]: value };
    const composed = [
      parts.before.trim(),
      parts.highlight.trim() ? '*' + parts.highlight.trim() + '*' : '',
      parts.after.trim(),
    ]
      .filter(Boolean)
      .join(' ');
    updateField('hero', {
      ...(config.hero || {}),
      titleBefore: parts.before,
      titleHighlight: parts.highlight,
      titleAfter: parts.after,
      title: composed,
    });
  };


  return (
    <>
      {/* ── Site Logo ──────────────────────────────────────────────── */}
      <div className={secCls('logo')}>
        <div className={styles.sectionHeader}>
          {secTitle('logo', 'Site Logo')}
        </div>
        <p style={{ fontSize: '13px', color: '#64748b', margin: '0 0 16px' }}>
          Upload logo images used in the header and footer across the website.
        </p>
        <div className={styles.heroTopGrid}>
          <div className={styles.field}>
            <label>Main Logo (dark backgrounds)</label>
            <div className={styles.heroBackgroundControlRow}>
              <div className={styles.heroBackgroundInputWrap}>
                <input
                  value={logo.mainUrl ?? ''}
                  placeholder="Upload or paste logo URL"
                  onChange={(e) => updateLogo('mainUrl', e.target.value)}
                />
              </div>
              <div className={styles.heroBackgroundButtons}>
                <label className={styles.uploadBtn}>
                  <Upload size={14} />
                  {uploadLoading ? 'Uploading...' : 'Upload'}
                  <input
                    type="file"
                    accept="image/png,image/jpeg,image/jpg,image/svg+xml,image/webp"
                    disabled={uploadLoading}
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      handleImageUpload(file, (url) => updateLogo('mainUrl', url));
                      e.target.value = '';
                    }}
                  />
                </label>
              </div>
            </div>
            {logo.mainUrl ? (
              <div style={{ marginTop: '8px' }}>
                <img
                  src={resolveImageUrl(logo.mainUrl)}
                  alt="Main logo preview"
                  style={{ maxHeight: '48px', objectFit: 'contain', borderRadius: '6px', background: '#1e293b', padding: '8px 12px' }}
                />
              </div>
            ) : null}
          </div>
          <div className={styles.field}>
            <label>Light Logo (transparent header)</label>
            <div className={styles.heroBackgroundControlRow}>
              <div className={styles.heroBackgroundInputWrap}>
                <input
                  value={logo.lightUrl ?? ''}
                  placeholder="Upload or paste light logo URL"
                  onChange={(e) => updateLogo('lightUrl', e.target.value)}
                />
              </div>
              <div className={styles.heroBackgroundButtons}>
                <label className={styles.uploadBtn}>
                  <Upload size={14} />
                  {uploadLoading ? 'Uploading...' : 'Upload'}
                  <input
                    type="file"
                    accept="image/png,image/jpeg,image/jpg,image/svg+xml,image/webp"
                    disabled={uploadLoading}
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      handleImageUpload(file, (url) => updateLogo('lightUrl', url));
                      e.target.value = '';
                    }}
                  />
                </label>
              </div>
            </div>
            {logo.lightUrl ? (
              <div style={{ marginTop: '8px' }}>
                <img
                  src={resolveImageUrl(logo.lightUrl)}
                  alt="Light logo preview"
                  style={{ maxHeight: '48px', objectFit: 'contain', borderRadius: '6px', background: '#f1f5f9', padding: '8px 12px' }}
                />
              </div>
            ) : null}
          </div>
        </div>
        <span className={styles.fieldHint}>Main logo is used on scrolled header &amp; footer. Light logo is used when the header overlays the hero image.</span>
      </div>

      <div className={secCls('hero')} style={!isSectionVisible('hero') ? { opacity: 0.5 } : undefined}>
        <div className={styles.sectionHeader}>
          {secTitle('hero', 'Hero Section')}
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <ToggleSwitch checked={isSectionVisible('hero')} onChange={() => toggleSection('hero')} />
            <span style={{ fontSize: '12px', fontWeight: 600, color: isSectionVisible('hero') ? '#15803d' : '#b91c1c' }}>{isSectionVisible('hero') ? 'Visible' : 'Hidden'}</span>
          </div>
        </div>
        <div className={styles.heroTopGrid}>
          <div className={styles.field}>
            <label>Badge Text</label>
            <input
              value={config.hero?.badgeText ?? ''}
              placeholder="e.g. Discover 10,000+ holidays worldwide"
              onChange={(e) => updateField('hero', { ...(config.hero || {}), badgeText: e.target.value })}
            />
          </div>
          <div className={styles.field}>
            <label>Search Button Text</label>
            <input
              value={config.hero?.searchButtonText ?? ''}
              placeholder="e.g. Search"
              onChange={(e) =>
                updateField('hero', { ...(config.hero || {}), searchButtonText: e.target.value })
              }
            />
          </div>
        </div>
        <div className={styles.heroContentGrid}>
          <div className={styles.field}>
            <label>Title</label>
            <div className={styles.heroTitleParts}>
              <div className={styles.heroTitlePart}>
                <span className={styles.heroTitlePartLabel}>Before</span>
                <input
                  value={heroTitleParts.before}
                  placeholder="e.g. Vind jouw"
                  onChange={(e) => setHeroTitlePart('before', e.target.value)}
                />
              </div>
              <div className={styles.heroTitlePart}>
                <span className={styles.heroTitlePartLabel}>Highlighted word</span>
                <input
                  className={styles.heroTitleHighlightInput}
                  value={heroTitleParts.highlight}
                  placeholder="e.g. zon"
                  onChange={(e) => setHeroTitlePart('highlight', e.target.value)}
                />
              </div>
              <div className={styles.heroTitlePart}>
                <span className={styles.heroTitlePartLabel}>After</span>
                <input
                  value={heroTitleParts.after}
                  placeholder="e.g. bestemming"
                  onChange={(e) => setHeroTitlePart('after', e.target.value)}
                />
              </div>
            </div>
            <span className={styles.fieldHint}>
              The website prints the highlighted word in the gold handwritten style, and
              only that word. Leave it empty for a title with no accent.
            </span>
          </div>
          <div className={styles.field}>
            <label>Subtitle</label>
            <textarea
              rows={3}
              value={config.hero?.subtitle ?? ''}
              placeholder="e.g. From relaxing beaches to vibrant city breaks, explore handpicked trips."
              onChange={(e) => updateField('hero', { ...(config.hero || {}), subtitle: e.target.value })}
            />
          </div>
        </div>
        {/* One banner per search tab. The hero sells whichever product the visitor is
            searching, so a beach behind a flights-only search is advertising the other one.
            Both fields behave identically, which is why they are one renderer called twice
            rather than the same sixty lines written out again. */}
        {heroImageField({
          field: 'backgroundImageUrl',
          label: 'Background Image (Packages)',
          hint: 'Shown behind the Packages tab, which is the tab the homepage opens on. Use a wide image for better hero coverage.',
        })}
        {heroImageField({
          field: 'flightBackgroundImageUrl',
          label: 'Background Image (Flights only)',
          hint: 'Shown behind the Flights-only tab. Leave empty to keep using the Packages image.',
        })}
      </div>

      <div className={secCls('search')}>
        <div className={styles.sectionHeader}>
          {secTitle('search', 'Search Fields')}
          <button type="button" className={styles.smallBtn} onClick={() => addListItem('searchFields', EMPTY_SEARCH_FIELD)}>
            <Plus size={14} />
            Add Field
          </button>
        </div>
        {ensureArray(config.searchFields).map((field, index) => (
          <div key={`search-${index}`} className={styles.itemCard}>
            <div className={styles.searchFieldRow}>
              <div className={`${styles.field} ${styles.searchFieldCell}`}>
                <label>Key</label>
                <input value={field.key ?? ''} placeholder="e.g. destination" onChange={(e) => updateListItem('searchFields', index, 'key', e.target.value)} />
              </div>
              <div className={`${styles.field} ${styles.searchFieldCell}`}>
                <label>Label</label>
                <input value={field.label ?? ''} placeholder="e.g. Destination" onChange={(e) => updateListItem('searchFields', index, 'label', e.target.value)} />
              </div>
              <div className={`${styles.field} ${styles.searchFieldCell}`}>
                <label>Value</label>
                <input value={field.value ?? ''} placeholder="e.g. Where to?" onChange={(e) => updateListItem('searchFields', index, 'value', e.target.value)} />
              </div>
              <button type="button" className={`${styles.dangerBtn} ${styles.searchFieldRemoveBtn}`} onClick={() => removeListItem('searchFields', index)}>
                <Trash2 size={14} />
                Remove
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* ── Featured Holiday Types — the homepage "Travel Categories" section ──
          Owns the section heading and the visibility toggle too; the separate
          "Travel Categories" card that used to hold them only added a list of
          fallback artwork the site no longer reaches. */}
      <div className={secCls('categories')} style={!isSectionVisible('categories') ? { opacity: 0.5 } : undefined}>
        <div className={styles.sectionHeader}>
          {secTitle('categories', 'Travel Categories')}
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <ToggleSwitch checked={isSectionVisible('categories')} onChange={() => toggleSection('categories')} />
            <span style={{ fontSize: '12px', fontWeight: 600, color: isSectionVisible('categories') ? '#15803d' : '#b91c1c' }}>{isSectionVisible('categories') ? 'Visible' : 'Hidden'}</span>
          </div>
          {/* The seed is spread with a fresh `destinations` array: the constant is
              module-level, so adding it by reference would have every new card
              share one array instance. */}
          <button
            type="button"
            className={styles.smallBtn}
            disabled={featuredFull}
            title={featuredFull ? `The homepage grid fits ${MAX_FEATURED_HOLIDAY_TYPES} cards` : undefined}
            onClick={() =>
              addListItem('featuredHolidayTypes', { ...EMPTY_FEATURED_HOLIDAY_TYPE, destinations: [] })
            }
          >
            <Plus size={14} />
            Add Holiday Type
          </button>
        </div>
        <p className={styles.sectionHint}>
          The heading and the cards for the homepage <strong>Travel Categories</strong> section.
          Pick up to {MAX_FEATURED_HOLIDAY_TYPES} holiday types — the grid fits exactly {MAX_FEATURED_HOLIDAY_TYPES}.
          The card name and link come from the holiday type; the image is set here.
          {featured.length === 0 && ' While none are selected, the homepage falls back to the first 4 holiday types.'}
        </p>

        {/* Section heading (sectionHeaders.categories) — lives here because it is
            the heading printed directly above these cards. */}
        <div className={styles.sectionHeaderFields}>
          <div className={styles.field}>
            <label>Section Tag</label>
            <input value={config.sectionHeaders?.categories?.tag ?? ''} placeholder="e.g. ✦ Explore" onChange={(e) => updateField('sectionHeaders', { ...(config.sectionHeaders || {}), categories: { ...(config.sectionHeaders?.categories || {}), tag: e.target.value } })} />
          </div>
          <div className={styles.field}>
            <label>Section Title</label>
            <input value={config.sectionHeaders?.categories?.title ?? ''} placeholder="e.g. Something for everyone" onChange={(e) => updateField('sectionHeaders', { ...(config.sectionHeaders || {}), categories: { ...(config.sectionHeaders?.categories || {}), title: e.target.value } })} />
          </div>
          <div className={styles.field}>
            <label>Section Subtitle</label>
            <input value={config.sectionHeaders?.categories?.subtitle ?? ''} placeholder="e.g. Find the perfect holiday..." onChange={(e) => updateField('sectionHeaders', { ...(config.sectionHeaders || {}), categories: { ...(config.sectionHeaders?.categories || {}), subtitle: e.target.value } })} />
          </div>
        </div>

        {featured.map((item, index) => (
          <div key={`fht-${index}`} className={styles.fhtCard}>
            {/* Header: identity + the controls that act on the whole card */}
            <div className={styles.fhtHeader}>
              <span className={styles.fhtIndex}>{index + 1}</span>
              <span className={styles.fhtTitle}>
                {item.title || <em className={styles.fhtUntitled}>No holiday type selected</em>}
              </span>
              <span className={styles.fhtHeaderActions}>
                <span className={styles.fhtStatus}>
                  <ToggleSwitch
                    checked={item.active !== false}
                    onChange={() => updateListItem('featuredHolidayTypes', index, 'active', item.active === false)}
                  />
                  <span className={item.active === false ? styles.fhtStatusOff : styles.fhtStatusOn}>
                    {item.active === false ? 'Hidden' : 'Visible'}
                  </span>
                </span>
                <span className={styles.fhtDivider} />
                <button
                  type="button"
                  className={styles.fhtIconBtn}
                  disabled={index === 0}
                  onClick={() => moveFeatured(index, -1)}
                  title="Move up"
                >
                  <ChevronUp size={15} />
                </button>
                <button
                  type="button"
                  className={styles.fhtIconBtn}
                  disabled={index === featured.length - 1}
                  onClick={() => moveFeatured(index, 1)}
                  title="Move down"
                >
                  <ChevronDown size={15} />
                </button>
                <button
                  type="button"
                  className={`${styles.fhtIconBtn} ${styles.fhtIconBtnDanger}`}
                  onClick={() => removeListItem('featuredHolidayTypes', index)}
                  title="Remove card"
                >
                  <Trash2 size={15} />
                </button>
              </span>
            </div>

            {/* Body: thumbnail beside the fields, so no column is left empty */}
            <div className={`${styles.fhtBody} ${item.active === false ? styles.fhtBodyMuted : ''}`}>
              <div className={styles.fhtThumb}>
                {item.imageUrl ? (
                  <img
                    src={resolveImageUrl(item.imageUrl)}
                    alt={item.title ? `${item.title} preview` : 'Holiday type image preview'}
                  />
                ) : (
                  <span className={styles.fhtThumbEmpty}>
                    <ImageIcon size={18} />
                    No image
                  </span>
                )}
              </div>
              <div className={styles.fhtFields}>
                <div className={styles.field}>
                  <label>Holiday Type</label>
                  <SelectBox
                    width="100%"
                    value={item.holidayTypeId != null ? String(item.holidayTypeId) : ''}
                    options={holidayTypeOptions}
                    placeholder={holidayTypeOptions.length ? 'Select a holiday type…' : 'Loading…'}
                    onChange={(e) => selectFeaturedType(index, e.target.value)}
                  />
                  {item.holidayTypeId == null && item.title && (
                    <span className={styles.fieldHint}>Saved as “{item.title}”</span>
                  )}
                </div>
                <div className={styles.field}>
                  <label>Card Image</label>
                  <div className={styles.imageUploadRow}>
                    <input
                      value={item.imageUrl ?? ''}
                      placeholder="Upload or paste image URL"
                      onChange={(e) => updateListItem('featuredHolidayTypes', index, 'imageUrl', e.target.value)}
                    />
                    <label className={styles.uploadBtn}>
                      <Upload size={14} />
                      {uploadLoading ? 'Uploading...' : 'Upload'}
                      <input
                        type="file"
                        accept="image/png,image/jpeg,image/jpg,image/svg+xml,image/webp"
                        disabled={uploadLoading}
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          handleImageUpload(file, (url) =>
                            updateListItem('featuredHolidayTypes', index, 'imageUrl', url)
                          );
                          e.target.value = '';
                        }}
                      />
                    </label>
                  </div>
                </div>
                {/* Master §12B.3: optional date range, on the travel START date only. */}
                <div className={styles.field}>
                  <label>
                    <input
                      type="checkbox"
                      checked={!!item.enableDateRange}
                      onChange={(e) => updateListItem('featuredHolidayTypes', index, 'enableDateRange', e.target.checked)}
                    />{' '}
                    Enable Date Range
                  </label>
                  {item.enableDateRange && (
                    <div className={styles.imageUploadRow}>
                      <input
                        type="date"
                        aria-label="Start Date"
                        value={item.startDate ?? ''}
                        onChange={(e) => updateListItem('featuredHolidayTypes', index, 'startDate', e.target.value)}
                      />
                      <input
                        type="date"
                        aria-label="End Date"
                        value={item.endDate ?? ''}
                        onChange={(e) => updateListItem('featuredHolidayTypes', index, 'endDate', e.target.value)}
                      />
                    </div>
                  )}
                  <span className={styles.fieldHint}>
                    {item.enableDateRange && (!item.startDate || !item.endDate || item.startDate > item.endDate)
                      ? 'Start and End are both needed, Start on or before End — saving is refused otherwise.'
                      : 'Limits the departure / check-in date only; the return may fall later. A card title (e.g. "2027") never restricts dates.'}
                  </span>
                </div>
              </div>
            </div>

            {/* Destinations revealed when this card is expanded on the homepage */}
            <div className={`${styles.fhtDests} ${item.active === false ? styles.fhtBodyMuted : ''}`}>
              <button
                type="button"
                className={styles.fhtDestsHead}
                aria-expanded={openDests.has(index)}
                onClick={() => toggleDests(index)}
              >
                <MapPin size={13} />
                <span className={styles.fhtDestsTitle}>Destinations</span>
                <span className={styles.fhtDestsSummary}>{destsSummary(item)}</span>
                <span className={styles.fhtDestsCount}>
                  {destsOf(item).length}/{MAX_FEATURED_DESTINATIONS}
                </span>
                <ChevronDown
                  size={15}
                  className={`${styles.fhtChev} ${openDests.has(index) ? styles.fhtChevOpen : ''}`}
                />
              </button>

              {openDests.has(index) && (
              <div className={styles.fhtDestsBody}>
              {/* The editable list comes first — it is what the holiday type page
                  actually renders whenever it is non-empty. */}
              <span className={styles.liveLabel}>
                {destsOf(item).length > 0
                  ? 'Shown on the holiday type page'
                  : 'Shown on the holiday type page — nothing set yet'}
              </span>
              {destsOf(item).length > 0 ? (
                <div className={styles.destChips}>
                  {destsOf(item).map((d, di) => (
                    <span key={`${destKey(d)}-${di}`} className={styles.destChip}>
                      <span className={styles.destChipOrder}>{di + 1}</span>
                      <span
                        className={`${styles.destChipKind} ${
                          d.type === 'country' ? styles.destChipKindCountry : styles.destChipKindCity
                        }`}
                      >
                        {d.type === 'country' ? 'Country' : 'City'}
                      </span>
                      <span className={styles.destChipName}>
                        {d.name}
                        {d.type === 'city' && d.countryName ? `, ${d.countryName}` : ''}
                      </span>
                      <span className={styles.destChipCode}>{d.code}</span>
                      <span className={styles.destChipBtns}>
                        <button
                          type="button"
                          className={styles.destChipBtn}
                          disabled={di === 0}
                          title="Move left"
                          onClick={() => moveDestination(index, di, -1)}
                        >
                          <ChevronLeft size={13} />
                        </button>
                        <button
                          type="button"
                          className={styles.destChipBtn}
                          disabled={di === destsOf(item).length - 1}
                          title="Move right"
                          onClick={() => moveDestination(index, di, 1)}
                        >
                          <ChevronRight size={13} />
                        </button>
                        <button
                          type="button"
                          className={`${styles.destChipBtn} ${styles.destChipRemove}`}
                          title="Remove destination"
                          onClick={() => removeDestination(index, di)}
                        >
                          <Trash2 size={13} />
                        </button>
                      </span>
                    </span>
                  ))}
                </div>
              ) : (
                <p className={styles.destEmpty}>
                  No destinations yet — the card links straight to the holiday type page.
                </p>
              )}
              <DestinationPicker
                disabled={destsOf(item).length >= MAX_FEATURED_DESTINATIONS}
                excludeKeys={new Set(destsOf(item).map(destKey))}
                onPick={(dest) => addDestination(index, dest)}
              />
              <span className={styles.fieldHint}>
                Pick up to {MAX_FEATURED_DESTINATIONS}. These are what
                {' '}<strong>/holidays/{slugForItem(item)}</strong> lists, and they also appear when
                the homepage card is expanded. Each one opens a filtered hotel search.
              </span>

              {/* The GeoData links. Only reachable by visitors while the list
                  above is empty, so the label has to say which case applies. */}
              <div className={styles.liveBlock}>
                <span className={styles.liveLabel}>
                  {destsOf(item).length > 0
                    ? 'Linked countries · currently hidden'
                    : 'Linked countries · shown on the website now'}
                </span>
                <HolidayTypeLiveCountries holidayTypeId={item.holidayTypeId} />
                <span className={styles.fieldHint}>
                  From <strong>GeoData → Countries</strong>.{' '}
                  {destsOf(item).length > 0
                    ? 'Replaced by your destinations above — remove all of them to show these instead.'
                    : 'Shown because no destinations are set above.'}
                </span>
              </div>
              </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* The old "Travel Categories" card lived here. Its heading fields and the
          visibility toggle moved into Featured Holiday Types above; its list of
          fallback artwork was removed because nothing on the site reached it —
          `count` rendered nowhere, and the artwork only applied to a featured
          card with no image of its own. The stored `categories` data is left
          untouched so the change is reversible and the site's offline fallback
          (used only when the holiday-types API is unreachable) still works. */}

      <div className={secCls('destinations')} style={!isSectionVisible('destinations') ? { opacity: 0.5 } : undefined}>
        <div className={styles.sectionHeader}>
          {secTitle('destinations', 'Destination Tabs')}
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <ToggleSwitch checked={isSectionVisible('destinations')} onChange={() => toggleSection('destinations')} />
            <span style={{ fontSize: '12px', fontWeight: 600, color: isSectionVisible('destinations') ? '#15803d' : '#b91c1c' }}>{isSectionVisible('destinations') ? 'Visible' : 'Hidden'}</span>
          </div>
          <button type="button" className={styles.smallBtn} onClick={() => addListItem('destinationTabs', EMPTY_DEST_TAB)}>
            <Plus size={14} />
            Add Tab
          </button>
        </div>
        <div className={styles.sectionHeaderFields}>
          <div className={styles.field}>
            <label>Section Tag</label>
            <input value={config.sectionHeaders?.destinations?.tag ?? ''} placeholder="e.g. ☀ Destinations" onChange={(e) => updateField('sectionHeaders', { ...(config.sectionHeaders || {}), destinations: { ...(config.sectionHeaders?.destinations || {}), tag: e.target.value } })} />
          </div>
          <div className={styles.field}>
            <label>Section Title</label>
            <input value={config.sectionHeaders?.destinations?.title ?? ''} placeholder="e.g. Our best sun destinations" onChange={(e) => updateField('sectionHeaders', { ...(config.sectionHeaders || {}), destinations: { ...(config.sectionHeaders?.destinations || {}), title: e.target.value } })} />
          </div>
          <div className={styles.field}>
            <label>Section Subtitle</label>
            <input value={config.sectionHeaders?.destinations?.subtitle ?? ''} placeholder="e.g. Handpicked destinations with guaranteed sunshine..." onChange={(e) => updateField('sectionHeaders', { ...(config.sectionHeaders || {}), destinations: { ...(config.sectionHeaders?.destinations || {}), subtitle: e.target.value } })} />
          </div>
        </div>
        {ensureArray(config.destinationTabs).map((tab, tabIndex) => (
          <div key={`tab-${tabIndex}`} className={styles.itemCard}>
            <div className={styles.tabHeaderRow}>
              <div className={`${styles.field} ${styles.tabNameField}`}>
                <label>Tab Name</label>
                <input
                  value={tab.tab ?? ''}
                  placeholder="e.g. Most Loved"
                  onChange={(e) => updateListItem('destinationTabs', tabIndex, 'tab', e.target.value)}
                />
              </div>
              <div className={styles.tabHeaderActions}>
                <button
                  type="button"
                  className={`${styles.smallBtn} ${styles.tabActionBtn}`}
                  onClick={() => {
                    const tabs = ensureArray(config.destinationTabs);
                    const next = tabs.map((entry, i) =>
                      i === tabIndex
                        ? { ...entry, cards: [...ensureArray(entry.cards), EMPTY_DEST_CARD] }
                        : entry
                    );
                    updateField('destinationTabs', next);
                  }}
                >
                  <Plus size={14} />
                  Add Card
                </button>
                <button
                  type="button"
                  className={`${styles.dangerBtn} ${styles.tabActionBtn}`}
                  onClick={() => removeListItem('destinationTabs', tabIndex)}
                >
                  <Trash2 size={14} />
                  Remove Tab
                </button>
              </div>
            </div>
            {ensureArray(tab.cards).map((card, cardIndex) => (
              <div key={`tab-${tabIndex}-card-${cardIndex}`} className={styles.nestedCard}>
                {/* Where the card sends the visitor. Without it the card is
                    decorative — the website renders it as plain, unclickable art. */}
                <div className={styles.field}>
                  <label>Links to</label>
                  {card.dest?.code ? (
                    <div className={styles.destChips}>
                      <span className={styles.destChip}>
                        <span
                          className={`${styles.destChipKind} ${
                            card.dest.type === 'country' ? styles.destChipKindCountry : styles.destChipKindCity
                          }`}
                        >
                          {card.dest.type === 'country' ? 'Country' : 'City'}
                        </span>
                        <span className={styles.destChipName}>
                          {card.dest.name}
                          {card.dest.type === 'city' && card.dest.countryName ? `, ${card.dest.countryName}` : ''}
                        </span>
                        <span className={styles.destChipCode}>{card.dest.code}</span>
                        <span className={styles.destChipBtns}>
                          <button
                            type="button"
                            className={`${styles.destChipBtn} ${styles.destChipRemove}`}
                            title="Remove link"
                            onClick={() => updateDestinationCardField(tabIndex, cardIndex, 'dest', null)}
                          >
                            <Trash2 size={13} />
                          </button>
                        </span>
                      </span>
                    </div>
                  ) : (
                    <p className={styles.destEmpty}>
                      Not linked — this card is not clickable on the website.
                    </p>
                  )}
                  {!card.dest?.code && (
                    <DestinationPicker
                      onPick={(dest) =>
                        // One update: also fill the display name when it is still
                        // blank, so a fresh card is usable after a single action.
                        patchDestinationCard(tabIndex, cardIndex, {
                          dest,
                          ...(String(card.name || '').trim() ? {} : { name: dest.name }),
                        })
                      }
                    />
                  )}
                  <span className={styles.fieldHint}>
                    Clicking this card opens a hotel search filtered to this country or city.
                  </span>
                </div>
                <div className={styles.itemGrid2}>
                  <div className={styles.field}>
                    <label>Destination Name</label>
                    <input
                      value={card.name ?? ''}
                      placeholder="e.g. Maldives"
                      onChange={(e) => updateDestinationCardField(tabIndex, cardIndex, 'name', e.target.value)}
                    />
                  </div>
                  <div className={styles.field}>
                    <label>Holidays Text</label>
                    <input
                      value={card.holidays ?? ''}
                      placeholder="e.g. 124 holidays"
                      onChange={(e) => updateDestinationCardField(tabIndex, cardIndex, 'holidays', e.target.value)}
                    />
                  </div>
                  <div className={styles.field}>
                    <label>Badge</label>
                    <SelectBox
                      width="100%"
                      placeholder="Select badge"
                      value={getBadgeSelectValue(card.badge ?? '')}
                      options={DESTINATION_BADGE_OPTIONS}
                      onChange={(e) => {
                        const selected = e.target.value;
                        const currentBadge = card.badge ?? '';
                        if (selected === BADGE_NONE_VALUE) {
                          updateDestinationCardField(tabIndex, cardIndex, 'badge', '');
                          return;
                        }
                        if (selected === BADGE_CUSTOM_VALUE) {
                          if (!currentBadge || isPresetBadge(currentBadge)) {
                            updateDestinationCardField(tabIndex, cardIndex, 'badge', '');
                          }
                          return;
                        }
                        updateDestinationCardField(tabIndex, cardIndex, 'badge', selected);
                      }}
                    />
                    {getBadgeSelectValue(card.badge ?? '') === BADGE_CUSTOM_VALUE && (
                      <input
                        className={styles.customBadgeInput}
                        placeholder="Type custom badge"
                        value={card.badge ?? ''}
                        onChange={(e) =>
                          updateDestinationCardField(tabIndex, cardIndex, 'badge', e.target.value)
                        }
                      />
                    )}
                    <span className={styles.fieldHint}>Badge is optional and shown as card highlight text.</span>
                  </div>
                  <div className={styles.field}>
                    <label>Image</label>
                    <div className={styles.imageUploadRow}>
                      <input
                        value={card.imageUrl ?? ''}
                        placeholder="Upload or paste image URL"
                        onChange={(e) =>
                          updateDestinationCardField(tabIndex, cardIndex, 'imageUrl', e.target.value)
                        }
                      />
                      <label className={styles.uploadBtn}>
                        <Upload size={14} />
                        {uploadLoading ? 'Uploading...' : 'Upload'}
                        <input
                          type="file"
                          accept="image/png,image/jpeg,image/jpg,image/svg+xml,image/webp"
                          disabled={uploadLoading}
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            handleImageUpload(file, (url) =>
                              updateDestinationCardField(tabIndex, cardIndex, 'imageUrl', url)
                            );
                            e.target.value = '';
                          }}
                        />
                      </label>
                    </div>
                    {card.imageUrl && (
                      <img
                        className={styles.imagePreview}
                        src={resolveImageUrl(card.imageUrl)}
                        alt={card.name ? `${card.name} preview` : 'Destination image preview'}
                      />
                    )}
                  </div>
                </div>
                <button
                  type="button"
                  className={styles.dangerBtn}
                  onClick={() => {
                    const tabs = ensureArray(config.destinationTabs);
                    const next = tabs.map((entry, i) => {
                      if (i !== tabIndex) return entry;
                      return {
                        ...entry,
                        cards: ensureArray(entry.cards).filter((_, ci) => ci !== cardIndex),
                      };
                    });
                    updateField('destinationTabs', next);
                  }}
                >
                  <Trash2 size={14} />
                  Remove Card
                </button>
              </div>
            ))}
          </div>
        ))}
      </div>

      <div className={secCls('vacationTypes')} style={!isSectionVisible('vacationTypes') ? { opacity: 0.5 } : undefined}>
        <div className={styles.sectionHeader}>
          {secTitle('vacationTypes', 'Favorite type of vacation')}
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <ToggleSwitch checked={isSectionVisible('vacationTypes')} onChange={() => toggleSection('vacationTypes')} />
            <span style={{ fontSize: '12px', fontWeight: 600, color: isSectionVisible('vacationTypes') ? '#15803d' : '#b91c1c' }}>{isSectionVisible('vacationTypes') ? 'Visible' : 'Hidden'}</span>
          </div>
          <button type="button" className={styles.smallBtn} onClick={() => addListItem('vacationTypes', EMPTY_VACATION)}>
            <Plus size={14} />
            Add Vacation Type
          </button>
        </div>
        <div className={styles.sectionHeaderFields}>
          <div className={styles.field}>
            <label>Section Tag</label>
            <input value={config.sectionHeaders?.vacationTypes?.tag ?? ''} placeholder="e.g. ♡ Curated" onChange={(e) => updateField('sectionHeaders', { ...(config.sectionHeaders || {}), vacationTypes: { ...(config.sectionHeaders?.vacationTypes || {}), tag: e.target.value } })} />
          </div>
          <div className={styles.field}>
            <label>Section Title</label>
            <input value={config.sectionHeaders?.vacationTypes?.title ?? ''} placeholder="e.g. Your favorite type of vacation" onChange={(e) => updateField('sectionHeaders', { ...(config.sectionHeaders || {}), vacationTypes: { ...(config.sectionHeaders?.vacationTypes || {}), title: e.target.value } })} />
          </div>
          <div className={styles.field}>
            <label>Section Subtitle</label>
            <input value={config.sectionHeaders?.vacationTypes?.subtitle ?? ''} placeholder="e.g. Curated experiences designed around how you love to travel." onChange={(e) => updateField('sectionHeaders', { ...(config.sectionHeaders || {}), vacationTypes: { ...(config.sectionHeaders?.vacationTypes || {}), subtitle: e.target.value } })} />
          </div>
        </div>
        {ensureArray(config.vacationTypes).map((item, index) => (
          <div key={`vac-${index}`} className={styles.itemCard}>
            <VacationSearchEditor
              criteria={item.search?.criteria}
              showFacts={item.search?.showFacts}
              boardCode={item.boardCode}
              active={openSection === 'vacationTypes'}
              onPatch={(patch) => patchVacationSearch(index, patch)}
            />
            <div className={styles.itemGrid3}>
              <div className={styles.field}>
                <label>Label</label>
                <input value={item.label ?? ''} placeholder="e.g. Couples" onChange={(e) => updateListItem('vacationTypes', index, 'label', e.target.value)} />
              </div>
              <div className={styles.field}>
                <label>Title</label>
                <input value={item.title ?? ''} placeholder="e.g. Romantic Getaways" onChange={(e) => updateListItem('vacationTypes', index, 'title', e.target.value)} />
              </div>
              <div className={styles.field}>
                <label>Image</label>
                <div className={styles.imageUploadRow}>
                  <input value={item.imageUrl ?? ''} placeholder="Upload or paste image URL" onChange={(e) => updateListItem('vacationTypes', index, 'imageUrl', e.target.value)} />
                  <label className={styles.uploadBtn}>
                    <Upload size={14} />
                    {uploadLoading ? 'Uploading...' : 'Upload'}
                    <input
                      type="file"
                      accept="image/png,image/jpeg,image/jpg,image/svg+xml,image/webp"
                      disabled={uploadLoading}
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        handleImageUpload(file, (url) => updateListItem('vacationTypes', index, 'imageUrl', url));
                        e.target.value = '';
                      }}
                    />
                  </label>
                </div>
                {item.imageUrl && (
                  <img
                    className={styles.imagePreview}
                    src={resolveImageUrl(item.imageUrl)}
                    alt={item.title ? `${item.title} preview` : 'Vacation image preview'}
                  />
                )}
              </div>
            </div>
            <div className={styles.itemGrid2}>
              <div className={styles.field}>
                <label>Button Text</label>
                <input value={item.buttonText ?? ''} placeholder="e.g. Explore" onChange={(e) => updateListItem('vacationTypes', index, 'buttonText', e.target.value)} />
              </div>
              <div className={styles.field}>
                <label>Description</label>
                <input value={item.description ?? ''} placeholder="Short description shown on the card" onChange={(e) => updateListItem('vacationTypes', index, 'description', e.target.value)} />
              </div>
            </div>
            <button type="button" className={styles.dangerBtn} onClick={() => removeListItem('vacationTypes', index)}>
              <Trash2 size={14} />
              Remove
            </button>
          </div>
        ))}
      </div>

      <div className={secCls('stats')} style={!isSectionVisible('stats') ? { opacity: 0.5 } : undefined}>
        <div className={styles.sectionHeader}>
          {secTitle('stats', 'Stats Strip')}
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <ToggleSwitch checked={isSectionVisible('stats')} onChange={() => toggleSection('stats')} />
            <span style={{ fontSize: '12px', fontWeight: 600, color: isSectionVisible('stats') ? '#15803d' : '#b91c1c' }}>{isSectionVisible('stats') ? 'Visible' : 'Hidden'}</span>
          </div>
          <button type="button" className={styles.smallBtn} onClick={() => addListItem('stats', EMPTY_STAT)}>
            <Plus size={14} />
            Add Stat
          </button>
        </div>
        <div className={styles.compactList}>
          {ensureArray(config.stats).map((item, index) => (
            <div key={`stat-${index}`} className={styles.itemCard}>
              <div className={styles.itemGrid2}>
                <div className={styles.field}>
                  <label>Value</label>
                  <input value={item.value ?? ''} placeholder="e.g. 10K+" onChange={(e) => updateListItem('stats', index, 'value', e.target.value)} />
                </div>
                <div className={styles.field}>
                  <label>Label</label>
                  <input value={item.label ?? ''} placeholder="e.g. Happy Travelers" onChange={(e) => updateListItem('stats', index, 'label', e.target.value)} />
                </div>
              </div>
              <button type="button" className={styles.dangerBtn} onClick={() => removeListItem('stats', index)}>
                <Trash2 size={14} />
                Remove
              </button>
            </div>
          ))}
        </div>
      </div>

      <div className={secCls('hotels')} style={!isSectionVisible('hotels') ? { opacity: 0.5 } : undefined}>
        <div className={styles.sectionHeader}>
          {secTitle('hotels', 'Popular Hotels')}
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <ToggleSwitch checked={isSectionVisible('hotels')} onChange={() => toggleSection('hotels')} />
            <span style={{ fontSize: '12px', fontWeight: 600, color: isSectionVisible('hotels') ? '#15803d' : '#b91c1c' }}>{isSectionVisible('hotels') ? 'Visible' : 'Hidden'}</span>
          </div>
          <button
            type="button"
            className={styles.smallBtn}
            disabled={hotelsFull}
            title={hotelsFull ? `The homepage row shows ${MAX_POPULAR_HOTELS} hotels` : undefined}
            onClick={() => addListItem('popularHotels', EMPTY_HOTEL)}
          >
            <Plus size={14} />
            Add Hotel
          </button>
        </div>
        <p className={styles.sectionHint}>
          The <strong>“Popular with our holidaymakers”</strong> row — {MAX_POPULAR_HOTELS} cards.
          Search your hotel database to pick each one: the name, location, star rating and image
          are filled in from the hotel record. <strong>Score</strong> and <strong>price</strong> are
          marketing values, so you set those yourself.
        </p>
        <div className={styles.sectionHeaderFields}>
          <div className={styles.field}>
            <label>Section Tag</label>
            <input value={config.sectionHeaders?.hotels?.tag ?? ''} placeholder="e.g. ★ Top Rated" onChange={(e) => updateField('sectionHeaders', { ...(config.sectionHeaders || {}), hotels: { ...(config.sectionHeaders?.hotels || {}), tag: e.target.value } })} />
          </div>
          <div className={styles.field}>
            <label>Section Title</label>
            <input value={config.sectionHeaders?.hotels?.title ?? ''} placeholder="e.g. Popular with our holidaymakers" onChange={(e) => updateField('sectionHeaders', { ...(config.sectionHeaders || {}), hotels: { ...(config.sectionHeaders?.hotels || {}), title: e.target.value } })} />
          </div>
          <div className={styles.field}>
            <label>Section Subtitle</label>
            <input value={config.sectionHeaders?.hotels?.subtitle ?? ''} placeholder="e.g. Top-rated hotels loved by thousands of happy travelers." onChange={(e) => updateField('sectionHeaders', { ...(config.sectionHeaders || {}), hotels: { ...(config.sectionHeaders?.hotels || {}), subtitle: e.target.value } })} />
          </div>
        </div>
        {popularHotels.map((item, index) => (
          <div key={`hotel-${index}`} className={styles.itemCard}>
            <div className={styles.pickerRow}>
              <div className={styles.field}>
                <label>Pick from hotel database — card {index + 1}</label>
                <HotelPicker onPick={(hotel, location) => applyHotelPick(index, hotel, location)} />
                {item.hotelId ? (
                  <span className={styles.fieldHint}>
                    Linked to hotel #{item.hotelId}. Fields below are pre-filled — edit them freely.
                  </span>
                ) : (
                  <span className={styles.fieldHint}>
                    Not linked to a hotel record yet — search above, or fill the fields in manually.
                  </span>
                )}
              </div>
              <div className={styles.field}>
                <label>Order</label>
                <div className={styles.imageUploadRow}>
                  <button type="button" className={styles.smallBtn} disabled={index === 0} onClick={() => moveHotel(index, -1)} title="Move left">
                    <ChevronUp size={14} />
                  </button>
                  <button type="button" className={styles.smallBtn} disabled={index === popularHotels.length - 1} onClick={() => moveHotel(index, 1)} title="Move right">
                    <ChevronDown size={14} />
                  </button>
                </div>
              </div>
            </div>
            <div className={styles.itemGrid3}>
              <div className={styles.field}>
                <label>Hotel Name</label>
                <input value={item.name ?? ''} placeholder="e.g. Sun Resort & Spa" onChange={(e) => updateListItem('popularHotels', index, 'name', e.target.value)} />
              </div>
              <div className={styles.field}>
                <label>Location</label>
                <input value={item.location ?? ''} placeholder="e.g. 🇹🇷 Antalya, Turkey" onChange={(e) => updateListItem('popularHotels', index, 'location', e.target.value)} />
              </div>
              <div className={styles.field}>
                <label>Image</label>
                <div className={styles.imageUploadRow}>
                  <input value={item.imageUrl ?? ''} placeholder="Upload or paste image URL" onChange={(e) => updateListItem('popularHotels', index, 'imageUrl', e.target.value)} />
                  <label className={styles.uploadBtn}>
                    <Upload size={14} />
                    {uploadLoading ? 'Uploading...' : 'Upload'}
                    <input
                      type="file"
                      accept="image/png,image/jpeg,image/jpg,image/svg+xml,image/webp"
                      disabled={uploadLoading}
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        handleImageUpload(file, (url) => updateListItem('popularHotels', index, 'imageUrl', url));
                        e.target.value = '';
                      }}
                    />
                  </label>
                </div>
                {item.imageUrl && (
                  <img
                    className={styles.imagePreview}
                    src={resolveImageUrl(item.imageUrl)}
                    alt={item.name ? `${item.name} preview` : 'Hotel image preview'}
                  />
                )}
              </div>
            </div>
            <div className={styles.itemGrid3}>
              <div className={styles.field}>
                <label>Price Text</label>
                <input value={item.price ?? ''} placeholder="e.g. €899" onChange={(e) => updateListItem('popularHotels', index, 'price', e.target.value)} />
                <span className={styles.fieldHint}>Shown as “From &lt;price&gt; p.p.” on the card.</span>
              </div>
              <div className={styles.field}>
                <label>Score</label>
                <input value={item.score ?? ''} placeholder="e.g. 9.2" onChange={(e) => updateListItem('popularHotels', index, 'score', e.target.value)} />
                <span className={styles.fieldHint}>The badge on the image. Keep the format consistent (example: 9.2).</span>
              </div>
              <div className={styles.field}>
                <label>Star Rating</label>
                <input
                  type="number"
                  min="1"
                  max="5"
                  value={item.stars ?? 5}
                  onChange={(e) => updateListItem('popularHotels', index, 'stars', Number(e.target.value) || 5)}
                />
                <span className={styles.fieldHint}>Filled from the hotel record when you pick one.</span>
              </div>
            </div>
            <button type="button" className={styles.dangerBtn} onClick={() => removeListItem('popularHotels', index)}>
              <Trash2 size={14} />
              Remove
            </button>
          </div>
        ))}
      </div>

      <div className={secCls('popularDest')} style={!isSectionVisible('popularDest') ? { opacity: 0.5 } : undefined}>
        <div className={styles.sectionHeader}>
          {secTitle('popularDest', 'Popular Destination Groups')}
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <ToggleSwitch checked={isSectionVisible('popularDest')} onChange={() => toggleSection('popularDest')} />
            <span style={{ fontSize: '12px', fontWeight: 600, color: isSectionVisible('popularDest') ? '#15803d' : '#b91c1c' }}>{isSectionVisible('popularDest') ? 'Visible' : 'Hidden'}</span>
          </div>
          <button
            type="button"
            className={styles.smallBtn}
            onClick={() => addListItem('popularDestinationGroups', EMPTY_GROUP)}
          >
            <Plus size={14} />
            Add Group
          </button>
        </div>
        <div className={styles.sectionHeaderFields}>
          <div className={styles.field}>
            <label>Section Tag</label>
            <input value={config.sectionHeaders?.popularDest?.tag ?? ''} placeholder="e.g. 🗺 Browse" onChange={(e) => updateField('sectionHeaders', { ...(config.sectionHeaders || {}), popularDest: { ...(config.sectionHeaders?.popularDest || {}), tag: e.target.value } })} />
          </div>
          <div className={styles.field}>
            <label>Section Title</label>
            <input value={config.sectionHeaders?.popularDest?.title ?? ''} placeholder="e.g. Most popular destinations" onChange={(e) => updateField('sectionHeaders', { ...(config.sectionHeaders || {}), popularDest: { ...(config.sectionHeaders?.popularDest || {}), title: e.target.value } })} />
          </div>
          <div className={styles.field}>
            <label>Section Subtitle</label>
            <input value={config.sectionHeaders?.popularDest?.subtitle ?? ''} placeholder="e.g. Browse our most searched and booked travel categories." onChange={(e) => updateField('sectionHeaders', { ...(config.sectionHeaders || {}), popularDest: { ...(config.sectionHeaders?.popularDest || {}), subtitle: e.target.value } })} />
          </div>
        </div>
        {ensureArray(config.popularDestinationGroups).map((item, index) => {
          const look = resolveGroupStyle(item, index);
          const links = ensureArray(item.links).map(asLinkObject);
          const viewAll = asLinkObject(item.viewAll);
          const viewAllUrl = groupLinkPreview({ ...viewAll, label: item.title || '' });
          const autoViewAllUrl = combinedSearchUrl(links, item.title || '');
          return (
          <div key={`group-${index}`} className={styles.itemCard}>
            <div className={styles.groupHead}>
              <div className={styles.field}>
                <label>Icon</label>
                <TravelIconPicker
                  kind="glyph"
                  label="Card icon"
                  value={isGlyph(item.icon) ? item.icon : ''}
                  autoName={look.autoGlyph}
                  tone={look.tone}
                  onChange={(name) => updateListItem('popularDestinationGroups', index, 'icon', name)}
                />
              </div>
              <div className={styles.groupHeadFields}>
                <div className={styles.itemGrid2}>
                  <div className={styles.field}>
                    <label>Group Title</label>
                    <input
                      value={item.title ?? ''}
                      placeholder="e.g. Europe Destinations"
                      onChange={(e) => updateListItem('popularDestinationGroups', index, 'title', e.target.value)}
                    />
                  </div>
                  <div className={styles.field}>
                    <label>Count Text</label>
                    <input
                      value={item.count ?? ''}
                      placeholder="e.g. 120+ places"
                      onChange={(e) => updateListItem('popularDestinationGroups', index, 'count', e.target.value)}
                    />
                  </div>
                </div>
                <div className={styles.field}>
                  <label>Card colour</label>
                  <ToneSwatches
                    value={item.tone}
                    autoTone={look.autoTone}
                    onChange={(tone) => updateListItem('popularDestinationGroups', index, 'tone', tone)}
                  />
                </div>
              </div>
            </div>
            <span className={styles.fieldHint}>
              Automatic picks the icon and colour from the title, and each link's icon from its
              text and place. Choose one to override it.
            </span>
            <div className={styles.field}>
              <label>
                Links
                <span className={styles.destChipCode}> {ensureArray(item.links).length}</span>
              </label>
              <span className={styles.fieldHint}>
                Each link can combine a place, a board type and a holiday type — e.g.
                “Turkey All Inclusive” is Turkey + All Inclusive, “France by Car” is
                France + Car Holidays. Leave all three blank to keep a plain label.
              </span>

              {links.map((l, li) => {
                const url = groupLinkPreview(l);
                return (
                  <div key={`glink-${index}-${li}`} className={styles.groupLinkRow}>
                    <div className={styles.groupLinkTop}>
                      <span className={styles.destChipOrder}>{li + 1}</span>
                      <TravelIconPicker
                        kind="emoji"
                        label="Link icon"
                        value={isEmoji(l.icon) ? l.icon : ''}
                        autoName={autoLinkIcon(l, item.title)}
                        onChange={(name) => patchGroupLink(index, li, { icon: name })}
                      />
                      <input
                        className={styles.groupLinkLabel}
                        value={l.label}
                        placeholder="Link text, e.g. Turkey All Inclusive"
                        onChange={(e) => patchGroupLink(index, li, { label: e.target.value })}
                      />
                      <button
                        type="button"
                        className={`${styles.destChipBtn} ${styles.destChipRemove}`}
                        title="Remove link"
                        onClick={() => removeGroupLink(index, li)}
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>

                    {linkFilterFields(l, (patch) => patchGroupLink(index, li, patch))}

                    <span className={styles.groupLinkUrl}>
                      {url ? `→ ${url}` : '→ not linked (plain text)'}
                    </span>
                  </div>
                );
              })}

              <button
                type="button"
                className={styles.smallBtn}
                onClick={() => addGroupLink(index)}
                style={{ marginTop: '8px' }}
              >
                <Plus size={14} />
                Add Link
              </button>
            </div>

            <div className={styles.field}>
              <label>“View all” link</label>
              <span className={styles.fieldHint}>
                The link under the card. Leave the text empty for “{defaultViewAllLabel(item.title) || 'View all'}”,
                and the filters empty to search every place in this card at once.
              </span>
              <div className={styles.groupLinkRow}>
                <div className={styles.groupLinkTop}>
                  <input
                    className={styles.groupLinkLabel}
                    value={viewAll.label}
                    placeholder={defaultViewAllLabel(item.title) || 'View all'}
                    aria-label="View all text"
                    onChange={(e) => patchViewAll(index, { label: e.target.value })}
                  />
                </div>
                {linkFilterFields(viewAll, (patch) => patchViewAll(index, patch))}
                <span className={styles.groupLinkUrl}>
                  {viewAllUrl
                    ? `→ ${viewAllUrl}`
                    : autoViewAllUrl
                      ? `→ ${autoViewAllUrl} (automatic: every place in this card)`
                      : '→ hidden until a link above has a place, board type or holiday type'}
                </span>
              </div>
            </div>

            <button
              type="button"
              className={styles.dangerBtn}
              onClick={() => removeListItem('popularDestinationGroups', index)}
            >
              <Trash2 size={14} />
              Remove
            </button>
          </div>
          );
        })}
      </div>

      <div className={secCls('trust')} style={!isSectionVisible('trust') ? { opacity: 0.5 } : undefined}>
        <div className={styles.sectionHeader}>
          {secTitle('trust', 'Trust Items')}
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <ToggleSwitch checked={isSectionVisible('trust')} onChange={() => toggleSection('trust')} />
            <span style={{ fontSize: '12px', fontWeight: 600, color: isSectionVisible('trust') ? '#15803d' : '#b91c1c' }}>{isSectionVisible('trust') ? 'Visible' : 'Hidden'}</span>
          </div>
          <button type="button" className={styles.smallBtn} onClick={() => addListItem('trustItems', EMPTY_TRUST)}>
            <Plus size={14} />
            Add Trust Item
          </button>
        </div>
        <div className={styles.sectionHeaderFields}>
          <div className={styles.field}>
            <label>Section Tag</label>
            <input value={config.sectionHeaders?.trust?.tag ?? ''} placeholder="e.g. ✓ Trust" onChange={(e) => updateField('sectionHeaders', { ...(config.sectionHeaders || {}), trust: { ...(config.sectionHeaders?.trust || {}), tag: e.target.value } })} />
          </div>
          <div className={styles.field}>
            <label>Section Title</label>
            <input value={config.sectionHeaders?.trust?.title ?? ''} placeholder="e.g. Why book with Sunsky?" onChange={(e) => updateField('sectionHeaders', { ...(config.sectionHeaders || {}), trust: { ...(config.sectionHeaders?.trust || {}), title: e.target.value } })} />
          </div>
          <div className={styles.field}>
            <label>Section Subtitle</label>
            <input value={config.sectionHeaders?.trust?.subtitle ?? ''} placeholder="e.g. Thousands of travelers trust us for stress-free holidays." onChange={(e) => updateField('sectionHeaders', { ...(config.sectionHeaders || {}), trust: { ...(config.sectionHeaders?.trust || {}), subtitle: e.target.value } })} />
          </div>
        </div>
        <p className={styles.fieldHint}>
          Upload a Logo or seal to make a row show that picture instead of an icon, on the
          guarantee bar near the top of the homepage and in this Trust section. Give the row a
          Link URL to make it clickable, anywhere you like, including another website. Rows
          left completely blank still fall back to the built-in insolvency insurance seal and
          VVR logo, in that order.
        </p>
        <div className={styles.compactList}>
          {ensureArray(config.trustItems).map((item, index) => (
            <div key={`trust-${index}`} className={styles.itemCard}>
              <div className={styles.itemGrid2}>
                <div className={styles.field}>
                  <label>Title</label>
                  <input value={item.title ?? ''} placeholder="e.g. Best Price Guarantee" onChange={(e) => updateListItem('trustItems', index, 'title', e.target.value)} />
                </div>
                <div className={styles.field}>
                  <label>Description</label>
                  <input value={item.description ?? ''} placeholder="e.g. Find a better deal and we will match it." onChange={(e) => updateListItem('trustItems', index, 'description', e.target.value)} />
                </div>
              </div>
              <div className={styles.field}>
                <label>Link URL (optional)</label>
                <input
                  value={item.url ?? ''}
                  placeholder="e.g. https://www.vvr.be, or /p/protection-insurance for a page on this site"
                  onChange={(e) => updateListItem('trustItems', index, 'url', e.target.value)}
                />
              </div>

              {/* A logo or seal for this row. Uploading one makes the row show that picture on
                  the website instead of an icon, in both the guarantee bar and the Trust
                  section. Leave it empty and the row behaves as before. */}
              <div className={styles.field}>
                <label>Logo or seal (optional)</label>
                <div className={styles.heroBackgroundControlRow}>
                  <div className={styles.heroBackgroundInputWrap}>
                    <input
                      value={item.imageUrl ?? ''}
                      placeholder="Upload, or paste an image URL"
                      onChange={(e) => updateListItem('trustItems', index, 'imageUrl', e.target.value)}
                    />
                  </div>
                  <div className={styles.heroBackgroundButtons}>
                    <label className={styles.uploadBtn}>
                      <Upload size={14} />
                      {uploadLoading ? 'Uploading...' : 'Upload'}
                      <input
                        type="file"
                        accept="image/png,image/jpeg,image/jpg,image/svg+xml,image/webp"
                        disabled={uploadLoading}
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          handleImageUpload(file, (url) =>
                            updateListItem('trustItems', index, 'imageUrl', url));
                          e.target.value = '';
                        }}
                      />
                    </label>
                  </div>
                </div>
                {item.imageUrl ? (
                  <div className={styles.imagePreviewWrap}>
                    <img
                      className={styles.heroImagePreview}
                      src={resolveImageUrl(item.imageUrl)}
                      alt={item.imageAlt || item.title || 'Trust logo preview'}
                    />
                  </div>
                ) : null}
              </div>

              {/* Screen readers and a failed image both fall back to this, so it matters most
                  on exactly the rows nobody checks. */}
              {item.imageUrl ? (
                <div className={styles.field}>
                  <label>Logo description (for screen readers)</label>
                  <input
                    value={item.imageAlt ?? ''}
                    placeholder="e.g. VVR — Vereniging Vlaamse Reisbureaus"
                    onChange={(e) => updateListItem('trustItems', index, 'imageAlt', e.target.value)}
                  />
                </div>
              ) : null}

              <button type="button" className={styles.dangerBtn} onClick={() => removeListItem('trustItems', index)}>
                <Trash2 size={14} />
                Remove
              </button>
            </div>
          ))}
        </div>
      </div>

      <div className={secCls('marquee')} style={!isSectionVisible('marquee') ? { opacity: 0.5 } : undefined}>
        <div className={styles.sectionHeader}>
          {secTitle('marquee', 'Marquee / Ticker Items')}
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <ToggleSwitch checked={isSectionVisible('marquee')} onChange={() => toggleSection('marquee')} />
            <span style={{ fontSize: '12px', fontWeight: 600, color: isSectionVisible('marquee') ? '#15803d' : '#b91c1c' }}>{isSectionVisible('marquee') ? 'Visible' : 'Hidden'}</span>
          </div>
          <button type="button" className={styles.smallBtn} onClick={() => addListItem('marqueeItems', '')}>
            <Plus size={14} />
            Add Ticker Item
          </button>
        </div>
        <p className={styles.fieldHint}>
          These lines are shown in the moving trust strip (e.g. 24/7 Support, Best Price Guarantee).
        </p>
        <div className={styles.marqueeList}>
          {ensureArray(config.marqueeItems).map((item, index) => (
            <MarqueeItemCard
              key={`marquee-${index}`}
              value={item}
              onChange={(text) => updatePrimitiveListItem('marqueeItems', index, text)}
              onRemove={() => removeListItem('marqueeItems', index)}
            />
          ))}
        </div>
      </div>

      <div className={secCls('reviews')} style={!isSectionVisible('reviews') ? { opacity: 0.5 } : undefined}>
        <div className={styles.sectionHeader}>
          {secTitle('reviews', 'Reviews')}
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <ToggleSwitch checked={isSectionVisible('reviews')} onChange={() => toggleSection('reviews')} />
            <span style={{ fontSize: '12px', fontWeight: 600, color: isSectionVisible('reviews') ? '#15803d' : '#b91c1c' }}>{isSectionVisible('reviews') ? 'Visible' : 'Hidden'}</span>
          </div>
          <button type="button" className={styles.smallBtn} onClick={() => addListItem('reviews', EMPTY_REVIEW)}>
            <Plus size={14} />
            Add Review
          </button>
        </div>
        {ensureArray(config.reviews).map((item, index) => (
          <div key={`review-${index}`} className={styles.itemCard}>
            <div className={styles.itemGrid3}>
              <div className={styles.field}>
                <label>Name</label>
                <input value={item.name ?? ''} placeholder="e.g. Sarah M." onChange={(e) => updateListItem('reviews', index, 'name', e.target.value)} />
              </div>
              <div className={styles.field}>
                <label>Date</label>
                <input value={item.date ?? ''} placeholder="e.g. Jan 2026" onChange={(e) => updateListItem('reviews', index, 'date', e.target.value)} />
              </div>
              <div className={styles.field}>
                <label>Score</label>
                <input value={item.score ?? ''} placeholder="e.g. 5/5" onChange={(e) => updateListItem('reviews', index, 'score', e.target.value)} />
              </div>
            </div>
            <div className={styles.itemGrid2}>
              <div className={styles.field}>
                <label>Avatar</label>
                <div className={styles.imageUploadRow}>
                  <input value={item.avatarUrl ?? ''} placeholder="Upload or paste avatar URL" onChange={(e) => updateListItem('reviews', index, 'avatarUrl', e.target.value)} />
                  <label className={styles.uploadBtn}>
                    <Upload size={14} />
                    {uploadLoading ? 'Uploading...' : 'Upload'}
                    <input
                      type="file"
                      accept="image/png,image/jpeg,image/jpg,image/svg+xml,image/webp"
                      disabled={uploadLoading}
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        handleImageUpload(file, (url) => updateListItem('reviews', index, 'avatarUrl', url));
                        e.target.value = '';
                      }}
                    />
                  </label>
                </div>
                {item.avatarUrl && (
                  <img
                    className={styles.avatarPreview}
                    src={resolveImageUrl(item.avatarUrl)}
                    alt={item.name ? `${item.name} avatar preview` : 'Review avatar preview'}
                  />
                )}
              </div>
              <div className={styles.field}>
                <label>Review Text</label>
                <input value={item.text ?? ''} placeholder="e.g. Smooth booking and excellent resort!" onChange={(e) => updateListItem('reviews', index, 'text', e.target.value)} />
              </div>
            </div>
            <button type="button" className={styles.dangerBtn} onClick={() => removeListItem('reviews', index)}>
              <Trash2 size={14} />
              Remove
            </button>
          </div>
        ))}
      </div>

      <div className={secCls('awards')} style={!isSectionVisible('awards') ? { opacity: 0.5 } : undefined}>
        <div className={styles.sectionHeader}>
          {secTitle('awards', 'Awards')}
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <ToggleSwitch checked={isSectionVisible('awards')} onChange={() => toggleSection('awards')} />
            <span style={{ fontSize: '12px', fontWeight: 600, color: isSectionVisible('awards') ? '#15803d' : '#b91c1c' }}>{isSectionVisible('awards') ? 'Visible' : 'Hidden'}</span>
          </div>
          <button type="button" className={styles.smallBtn} onClick={() => addListItem('awards', EMPTY_AWARD)}>
            <Plus size={14} />
            Add Award
          </button>
        </div>
        <div className={styles.compactList}>
          {ensureArray(config.awards).map((item, index) => (
            <div key={`award-${index}`} className={styles.itemCard}>
              <div className={styles.itemGrid2}>
                <div className={styles.field}>
                  <label>Award Title</label>
                  <input value={item.title ?? ''} placeholder="e.g. World Travel Awards" onChange={(e) => updateListItem('awards', index, 'title', e.target.value)} />
                </div>
                <div className={styles.field}>
                  <label>Year / Source</label>
                  <input value={item.year ?? ''} placeholder="e.g. 2025" onChange={(e) => updateListItem('awards', index, 'year', e.target.value)} />
                </div>
              </div>
              <button type="button" className={styles.dangerBtn} onClick={() => removeListItem('awards', index)}>
                <Trash2 size={14} />
                Remove
              </button>
            </div>
          ))}
        </div>
      </div>

      <div className={secCls('newsletter')} style={!isSectionVisible('newsletter') ? { opacity: 0.5 } : undefined}>
        <div className={styles.sectionHeader}>
          {secTitle('newsletter', 'Newsletter')}
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <ToggleSwitch checked={isSectionVisible('newsletter')} onChange={() => toggleSection('newsletter')} />
            <span style={{ fontSize: '12px', fontWeight: 600, color: isSectionVisible('newsletter') ? '#15803d' : '#b91c1c' }}>{isSectionVisible('newsletter') ? 'Visible' : 'Hidden'}</span>
          </div>
        </div>
        <div className={styles.itemGrid2}>
          <div className={styles.field}>
            <label>Title</label>
            <input
              value={config.newsletter?.title ?? ''}
              placeholder="e.g. Never miss a sunny deal"
              onChange={(e) => updateField('newsletter', { ...(config.newsletter || {}), title: e.target.value })}
            />
          </div>
          <div className={styles.field}>
            <label>Button Text</label>
            <input
              value={config.newsletter?.buttonText ?? ''}
              placeholder="e.g. Subscribe"
              onChange={(e) =>
                updateField('newsletter', { ...(config.newsletter || {}), buttonText: e.target.value })
              }
            />
          </div>
        </div>
        <div className={styles.itemGrid2}>
          <div className={styles.field}>
            <label>Subtitle</label>
            <textarea
              rows={2}
              value={config.newsletter?.subtitle ?? ''}
              placeholder="e.g. Subscribe for exclusive offers and travel inspiration."
              onChange={(e) =>
                updateField('newsletter', { ...(config.newsletter || {}), subtitle: e.target.value })
              }
            />
          </div>
          <div className={styles.field}>
            <label>Email Input Placeholder</label>
            <input
              value={config.newsletter?.inputPlaceholder ?? ''}
              placeholder="e.g. Your email address"
              onChange={(e) =>
                updateField('newsletter', { ...(config.newsletter || {}), inputPlaceholder: e.target.value })
              }
            />
          </div>
        </div>
      </div>

      {/* Results page headings (Levent 28 Oct). The website builds the heading itself
          ("Ontdek onze vakanties op Cyprus", "Ontdek onze stedentrips naar Rome"); a row here
          replaces it, word for word, for one vacation type/theme, country or destination. */}
      <div className={secCls('resultsHeadings')}>
        <div className={styles.sectionHeader}>
          {secTitle('resultsHeadings', 'Results page headings')}
          <button
            type="button"
            className={styles.smallBtn}
            onClick={() => updateField('sectionHeaders', {
              ...(config.sectionHeaders || {}),
              resultsHeadings: [...(Array.isArray(config.sectionHeaders?.resultsHeadings) ? config.sectionHeaders.resultsHeadings : []), { kind: 'type', code: '', nl: '', en: '' }],
            })}
          >
            <Plus size={14} />
            Add Heading
          </button>
        </div>
        <div className={styles.compactList}>
        <p className={styles.sectionHint}>
          Optional. Without a row the results page writes the heading automatically. Use a vacation
          type or theme by its name (e.g. Stedentrips, All Inclusive), a country by its code (e.g. TR)
          or a destination by its code (e.g. PMI). A destination or country row wins over a type row.
        </p>
        {(Array.isArray(config.sectionHeaders?.resultsHeadings) ? config.sectionHeaders.resultsHeadings : []).map((row, index, rows) => {
          const setRow = (patch) => updateField('sectionHeaders', {
            ...(config.sectionHeaders || {}),
            resultsHeadings: rows.map((r, i) => (i === index ? { ...r, ...patch } : r)),
          });
          return (
            <div key={`rh-${index}`} className={styles.itemCard}>
              <div className={styles.itemGrid2}>
              <div className={styles.field}>
                <label>Applies to</label>
                <select value={row.kind || 'type'} onChange={(e) => setRow({ kind: e.target.value })}>
                  <option value="type">Vacation type / theme (name)</option>
                  <option value="country">Country (code)</option>
                  <option value="destination">Destination (code)</option>
                </select>
              </div>
              <div className={styles.field}>
                <label>{(row.kind || 'type') === 'type' ? 'Type or theme name' : 'Code'}</label>
                <input value={row.code ?? ''} placeholder={(row.kind || 'type') === 'type' ? 'e.g. Stedentrips' : 'e.g. TR'} onChange={(e) => setRow({ code: e.target.value })} />
              </div>
              <div className={styles.field}>
                <label>Heading (NL)</label>
                <input value={row.nl ?? ''} placeholder="e.g. Ontdek onze vakanties op Cyprus" onChange={(e) => setRow({ nl: e.target.value })} />
              </div>
              <div className={styles.field}>
                <label>Heading (EN)</label>
                <input value={row.en ?? ''} placeholder="e.g. Discover our holidays in Cyprus" onChange={(e) => setRow({ en: e.target.value })} />
              </div>
              </div>
              <button
                type="button"
                className={styles.dangerBtn}
                onClick={() => updateField('sectionHeaders', { ...(config.sectionHeaders || {}), resultsHeadings: rows.filter((_, i) => i !== index) })}
              >
                <Trash2 size={14} />
                Remove
              </button>
            </div>
          );
        })}
        </div>
      </div>
    </>
  );
}
